/*
 * Cọc → Đẩy qua POS (không dùng ví khách) — logic thuần.
 * Tạo cọc, báo biên lai, chưa khớp, xác nhận đã có API (xem api.ts); đẩy POS, hoàn, bỏ cọc vẫn lưu trên máy chờ API.
 *
 * 1. Telesales gửi yêu cầu cọc kèm báo giá, khách chuyển khoản, gửi biên lai.
 * 2. Kế toán khớp sao kê → xác nhận đã nhận cọc (ghi mã sao kê).
 * 3. Lệnh "Đẩy qua POS": tạo đơn hàng cọc cho khách từ báo giá — tổng đơn = giá báo, đã trả = tiền cọc,
 *    còn thu = tổng − cọc. Phần thu ngân trừ cọc, thu phần còn lại do POS làm (không làm ở đây).
 * 4. Chưa đẩy POS mà khách không đến: hoàn cọc hoặc khách bỏ cọc (có lý do).
 *
 * Khi nối API: lệnh đẩy = gửi posPayload(row) để tạo đơn POS loại DEPOSIT
 * (lượt thu đầu BANK_TRANSFER, is_deposit = true, transaction_code = mã sao kê).
 */
import dayjs from 'dayjs';
import { uid, type LeadLocal, type Touch } from './logic';
import { checkQuote, money, type DepPurpose, type Deposit, type DepositCustomer, type DepositOrder, type DepSt, type Quote, type QuoteSvc } from './quote';

/** Một khoản cọc server trả về */
export interface ServerDeposit {
  id: number;
  code: string;
  customer_id?: number;
  quote_code?: string | null;
  amount?: number | string;
  purpose?: string;
  due_at?: string | null;
  note?: string | null;
  status: 'waiting' | 'checking' | 'received' | 'cancelled' | 'refund_pending';
  receipt_at?: string | null;
  reject_note?: string | null;
  received_at?: string | null;
  received_by_name?: string | null;
  bank_ref?: string | null;
  created_by_name?: string | null;
  created_at?: string | null;
  /** Khoản cũ tạo trước khi có API báo giá thì null, chỉ còn quote_code */
  quote_id?: number | null;
}

/** Một báo giá server trả về */
export interface ServerQuote {
  id: number;
  code: string;
  customer_id?: number;
  dich_vu?: string | null;
  items: { id?: number | null; name: string; price: number | string | null; card?: QuoteSvc['card'] | null }[];
  promo?: string | null;
  discount?: number | string | null;
  reason?: string | null;
  channel?: string | null;
  send_at?: string | null;
  valid_until?: string | null;
  status: Quote['status'];
  deposit_request?: { amount: number | string; purpose: string; due_at?: string | null } | null;
  deposit?: ServerDeposit | null;
  cancel_reason?: string | null;
  replaces_id?: number | null;
  replaced_by_id?: number | null;
  by?: string | null;
  at?: string | null;
}
/** Thao tác trên khoản cọc đã có API */
export type DepositApiAction = Extract<DepositAction, { type: 'receipt' | 'reject' | 'confirm' }>;

/** Trạng thái chỉ có trên máy (server chưa biết hoàn, bỏ, trừ cọc): không ghi đè */
const LOCAL_ONLY: DepSt[] = ['used', 'refunded', 'forfeited'];
const ms = (v?: string | null) => (v ? dayjs(v).valueOf() : undefined);

/** Đổi dữ liệu server sang dạng FE, giữ phần chỉ có trên máy (đơn POS, lý do hoàn...) */
export const depositFromServer = (s: ServerDeposit, base?: Deposit): Deposit => ({
  ...base,
  serverId: s.id,
  code: s.code,
  amount: s.amount != null ? Number(s.amount) : base?.amount ?? 0,
  purpose: (s.purpose ?? base?.purpose ?? 'Giữ lịch') as DepPurpose,
  due: ms(s.due_at) ?? base?.due ?? Date.now(),
  note: s.note ?? base?.note ?? '',
  status: base && LOCAL_ONLY.includes(base.status) ? base.status : s.status,
  receiptAt: ms(s.receipt_at),
  rejectNote: s.reject_note ?? undefined,
  receivedAt: ms(s.received_at),
  receivedBy: s.received_by_name ?? undefined,
  bankRef: s.bank_ref ?? undefined,
  by: s.created_by_name ?? base?.by,
  createdAt: ms(s.created_at) ?? base?.createdAt,
});

/**
 * Gộp cọc server vào báo giá trên máy theo quote_code.
 * Máy này chưa có báo giá thì dựng dòng từ mã báo giá (orphans) để đổi máy vẫn thấy cọc; dòng này không lưu vào lịch sử báo giá.
 */
export const mergeServerDeposits = (quotes: Quote[], items: ServerDeposit[], cust: DepositCustomer) => {
  const orphans: Quote[] = [];
  let next = quotes;
  items.forEach((s) => {
    const i = s.quote_id != null && next.some((q) => q.serverId === s.quote_id)
      ? next.findIndex((q) => q.serverId === s.quote_id)
      : next.findIndex((q) => q.code === s.quote_code);
    if (i >= 0) {
      next = next.map((q, n) => (n === i ? { ...q, deposit: { ...depositFromServer(s, q.deposit), cust: q.deposit?.cust ?? cust } } : q));
      return;
    }
    const deposit = { ...depositFromServer(s), cust };
    const at = deposit.createdAt ?? Date.now();
    orphans.push({ id: `srv-${s.id}`, code: s.quote_code || '—', at, by: deposit.by ?? '', dichVu: 'Cấy tóc', items: [], discount: 0, channel: 'Zalo', sendAt: at, validUntil: deposit.due, status: 'sent', deposit });
  });
  return { quotes: next, orphans };
};

/** Đổi báo giá server sang dạng FE; cọc giữ phần chỉ có trên máy. Chưa gửi thì chưa có khoản cọc, chỉ có yêu cầu */
export const quoteFromServer = (s: ServerQuote, base: Quote | undefined, cust: DepositCustomer): Quote => {
  const at = ms(s.at) ?? base?.at ?? Date.now();
  let deposit: Deposit | undefined;
  if (s.deposit) deposit = { ...depositFromServer(s.deposit, base?.deposit), cust: base?.deposit?.cust ?? cust };
  else if (s.deposit_request) {
    deposit = {
      code: '—', amount: Number(s.deposit_request.amount), purpose: s.deposit_request.purpose as DepPurpose,
      due: ms(s.deposit_request.due_at) ?? at, status: 'waiting', note: '', cust,
    };
  }
  return {
    id: base?.id ?? `srv-q${s.id}`,
    serverId: s.id,
    code: s.code,
    at,
    by: s.by ?? base?.by ?? '',
    dichVu: (s.dich_vu ?? base?.dichVu ?? 'Cấy tóc') as Quote['dichVu'],
    items: (s.items ?? []).map((x) => ({ id: x.id ?? undefined, name: x.name, price: x.price == null ? null : Number(x.price), ...(x.card ? { card: x.card } : {}) })),
    promo: s.promo ?? undefined,
    discount: Number(s.discount ?? 0),
    reason: s.reason ?? undefined,
    channel: (s.channel ?? 'Zalo') as Quote['channel'],
    sendAt: ms(s.send_at) ?? at,
    validUntil: ms(s.valid_until) ?? at,
    status: s.status,
    deposit,
    cancelReason: s.cancel_reason ?? undefined,
  };
};

/** Báo giá server thay bản trên máy theo serverId; báo giá cũ chỉ có trên máy thì giữ nguyên, không tự chuyển lên server */
export const mergeServerQuotes = (cur: Quote[], items: ServerQuote[], cust: DepositCustomer): Quote[] => {
  const mapped = items.map((s) => quoteFromServer(s, cur.find((q) => q.serverId === s.id), cust));
  const withLinks = mapped.map((q, i) => {
    const s = items[i];
    const by = s.replaced_by_id != null ? mapped.find((x) => x.serverId === s.replaced_by_id) : undefined;
    return { ...q, ...(by ? { replacedBy: by.code } : {}), ...(s.replaces_id != null ? { replaces: `srv-q${s.replaces_id}`, replacesId: s.replaces_id } : {}) };
  });
  // Bản chỉ có trên máy mà đã có bản thay trên server: bản thay gửi rồi thì bản này mới "Đã thay thế"
  const local = cur.filter((q) => !q.serverId).map((q) => {
    const nx = q.nextServerId != null ? withLinks.find((x) => x.serverId === q.nextServerId) : undefined;
    return nx && q.status !== 'cancelled' && q.status !== 'replaced' && ['sent', 'replied', 'replaced'].includes(nx.status)
      ? { ...q, status: 'replaced' as const, replacedBy: nx.code }
      : q;
  });
  return [...withLinks, ...local].sort((a, b) => b.at - a.at);
};

/** Một khoản cọc kèm vị trí trong hồ sơ khách */
export interface DepositRow { leadId: number; quote: Quote; deposit: Deposit }
export const allDeposits = (byId: Record<number, LeadLocal>): DepositRow[] =>
  Object.entries(byId).flatMap(([id, l]) => (l.quotes ?? [])
    // Báo giá trên server chưa gửi thì mới là yêu cầu cọc, chưa có khoản để đối soát
    .filter((q) => q.deposit && (!q.serverId || q.deposit.serverId))
    .map((q) => ({ leadId: Number(id), quote: q, deposit: q.deposit! })));

/** Mã đơn DH-MMDD-NN, đánh số chung mọi khách */
export const nextOrderCode = (byId: Record<number, LeadLocal>, at: number) => {
  const d = `DH-${dayjs(at).format('MMDD')}-`;
  const n = allDeposits(byId).filter((r) => r.deposit.order?.code.startsWith(d)).length + 1;
  return `${d}${String(n).padStart(2, '0')}`;
};

/** Đơn hàng cọc tạo từ báo giá khi đẩy qua POS */
export const orderFromQuote = (q: Quote, d: Deposit, code: string, by: string, at: number): DepositOrder => {
  const total = checkQuote(q.items, q.discount, q.reason).after ?? 0;
  return { code, at, by, items: q.items.map((x) => x.name), total, deposit: d.amount, status: 'deposit' };
};

/** Dữ liệu gửi sang POS khi nối API (tạo đơn loại DEPOSIT, lượt thu đầu = tiền cọc chuyển khoản) */
export const posPayload = (r: DepositRow) => {
  const { deposit: d, quote: q } = r;
  const c = checkQuote(q.items, q.discount, q.reason);
  return {
    customer_id: r.leadId,
    order_code: d.order?.code,
    quote_code: q.code,
    deposit_code: d.code,
    items: q.items.map((x) => ({ name: x.name, price: x.price ?? 0, quantity: 1 })),
    discount_percent: q.discount,
    promotion: q.promo ?? null,
    final_amount: c.after ?? 0,
    payment: { amount: d.amount, payment_method: 'BANK_TRANSFER' as const, is_deposit: true, transaction_code: d.bankRef || d.code },
    note: `Cọc ${d.code} · ${d.purpose} · báo giá ${q.code} · hạn ưu đãi ${dayjs(q.validUntil).format('DD/MM/YYYY')}`,
  };
};

export type DepositAction =
  | { type: 'receipt' }
  | { type: 'reject'; note: string }
  | { type: 'confirm'; bankRef?: string }
  | { type: 'push' }
  | { type: 'refund'; reason: string }
  | { type: 'forfeit'; reason: string };

export type ById = Record<number, LeadLocal>;
export interface DepositResult { byId: ById; error?: string }

const touch = (text: string, by: string, at: number): Touch => ({ id: uid(), at, kind: 'note', text, by });

/** Đổi khoản cọc của 1 báo giá + ghi lịch sử chạm của khách */
const withDeposit = (byId: ById, leadId: number, quoteId: string, d: Deposit, log: string, by: string, at: number): ById => {
  const l = byId[leadId];
  return {
    ...byId,
    [leadId]: {
      ...l,
      quotes: (l.quotes ?? []).map((q) => (q.id === quoteId ? { ...q, deposit: d } : q)),
      touches: [touch(log, by, at), ...l.touches],
    },
  };
};

export function depositAction(byId: ById, leadId: number, quoteId: string, act: DepositAction, by: string, at = Date.now()): DepositResult {
  const q = byId[leadId]?.quotes?.find((x) => x.id === quoteId);
  const d = q?.deposit;
  if (!q || !d) return { byId, error: 'Không tìm thấy khoản cọc' };
  const fail = (error: string) => ({ byId, error });

  switch (act.type) {
    case 'receipt':
      if (d.status !== 'waiting') return fail('Khoản cọc không ở trạng thái Chờ chuyển khoản');
      return { byId: withDeposit(byId, leadId, quoteId, { ...d, status: 'checking', receiptAt: at, rejectNote: undefined }, `Khách gửi biên lai cọc ${d.code} → chờ Kế toán đối soát`, by, at) };
    case 'reject':
      if (d.status !== 'checking') return fail('Chỉ trả lại được khoản đang chờ đối soát');
      if (!act.note.trim()) return fail('Ghi lý do không khớp');
      return { byId: withDeposit(byId, leadId, quoteId, { ...d, status: 'waiting', rejectNote: act.note.trim() }, `Kế toán: cọc ${d.code} chưa khớp sao kê — ${act.note.trim()}`, by, at) };
    case 'confirm': {
      if (d.status !== 'waiting' && d.status !== 'checking') return fail('Khoản cọc đã xử lý rồi');
      const next: Deposit = { ...d, status: 'received', receivedAt: at, receivedBy: by, bankRef: act.bankRef?.trim() || undefined, rejectNote: undefined };
      return { byId: withDeposit(byId, leadId, quoteId, next, `Kế toán xác nhận đã nhận cọc ${d.code} · ${money(d.amount)}`, by, at) };
    }
    case 'push': {
      if (d.status !== 'received') return fail('Kế toán chưa xác nhận đã nhận cọc');
      if (d.order) return fail(`Đã đẩy qua POS (đơn ${d.order.code})`);
      const order = orderFromQuote(q, d, nextOrderCode(byId, at), by, at);
      if (order.total < d.amount) return fail(`Tiền cọc lớn hơn giá báo (${money(order.total)}) — kiểm tra lại báo giá`);
      return {
        byId: withDeposit(byId, leadId, quoteId, { ...d, order }, `Đẩy cọc ${d.code} qua POS → đơn ${order.code} · tổng ${money(order.total)}, đã cọc ${money(d.amount)}, còn thu ${money(order.total - d.amount)}`, by, at),
      };
    }
    case 'refund':
    case 'forfeit': {
      if (d.status !== 'received') return fail('Chỉ hoàn / bỏ cọc khi Kế toán đã nhận cọc');
      if (d.order) return fail(`Cọc đã đẩy qua POS (đơn ${d.order.code}) — xử lý hoàn / bỏ cọc bên POS`);
      if (!act.reason.trim()) return fail('Ghi lý do');
      const label = act.type === 'refund' ? 'Hoàn cọc' : 'Khách bỏ cọc';
      const next: Deposit = { ...d, status: act.type === 'refund' ? 'refunded' : 'forfeited', closedAt: at, closedBy: by, closeReason: act.reason.trim() };
      return { byId: withDeposit(byId, leadId, quoteId, next, `${label} ${d.code} · ${money(d.amount)} — ${act.reason.trim()}`, by, at) };
    }
    default:
      return fail('Thao tác không hợp lệ');
  }
}
